"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { ASSET_CATEGORIES, ASSET_CATEGORY_ORDER, PRACTICAL_ASSETS, type AssetCategory, type PracticalAsset } from "@/lib/assets";
import { track } from "@/lib/analytics";

function haystack(a: PracticalAsset): string {
  return `${a.title} ${a.description} ${ASSET_CATEGORIES[a.category].label}`.toLowerCase();
}
function matches(hay: string, tokens: string[]): boolean {
  return tokens.every((tok) => hay.includes(tok));
}

export function AssetsExplorer() {
  const [active, setActive] = useState<AssetCategory | "all">("all");
  const [query, setQuery] = useState("");

  // Allow deep links like /assets/?cat=checklist or /assets/?q=permit.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("cat") as AssetCategory | null;
    if (cat && ASSET_CATEGORY_ORDER.includes(cat)) setActive(cat);
    const q = params.get("q") || params.get("search") || "";
    if (q.trim()) setQuery(q.trim());
  }, []);

  const q = query.trim().toLowerCase();
  const tokens = q ? q.split(/\s+/) : [];
  const searching = tokens.length > 0;

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: PRACTICAL_ASSETS.length };
    for (const cat of ASSET_CATEGORY_ORDER) c[cat] = PRACTICAL_ASSETS.filter((a) => a.category === cat).length;
    return c;
  }, []);

  // When searching, match the whole set (findability beats category scoping). With no
  // query, the category pill scopes the list; "all" shows the grouped, category-led view.
  const filtered = useMemo(
    () =>
      PRACTICAL_ASSETS.filter((a) => {
        const queryOk = !searching || matches(haystack(a), tokens);
        const catOk = searching || active === "all" || a.category === active;
        return queryOk && catOk;
      }),
    [active, searching, q],
  );
  const resultCount = filtered.length;
  const grouped = !searching && active === "all";

  // Debounced search-intent capture — what people look for and what returns nothing.
  const lastTracked = useRef("");
  useEffect(() => {
    if (!searching || q.length < 2) return;
    const id = setTimeout(() => {
      if (lastTracked.current === q) return;
      lastTracked.current = q;
      track("asset_search", { query: q, results: resultCount });
      if (resultCount === 0) track("asset_search_no_results", { query: q });
    }, 650);
    return () => clearTimeout(id);
  }, [q, searching, resultCount]);

  const groups = grouped
    ? ASSET_CATEGORY_ORDER.map((cat) => ({ cat, assets: PRACTICAL_ASSETS.filter((a) => a.category === cat) })).filter((g) => g.assets.length > 0)
    : [];

  function onCardClick(e: React.MouseEvent) {
    const a = (e.target as HTMLElement).closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    track("asset_card_clicked", { query: q, href, category: active });
  }

  return (
    <div className="bg-surface">
      {/* Filter bar */}
      <div className="border-b border-line bg-surface">
        <div className="mx-auto max-w-7xl px-5 py-6">
          <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search resources — e.g. checklist, permit, hot work, review"
              aria-label="Search practical RAMS resources"
              className="w-full rounded-xl border border-line bg-white px-4 py-3 text-base text-ink focus:border-emerald focus:outline-none"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill label="All resources" count={counts.all} on={active === "all" && !searching} onClick={() => { setQuery(""); setActive("all"); }} />
            {ASSET_CATEGORY_ORDER.map((cat) => (
              <Pill
                key={cat}
                label={ASSET_CATEGORIES[cat].label}
                count={counts[cat]}
                on={!searching && active === cat}
                onClick={() => {
                  setQuery("");
                  setActive(cat);
                  track("asset_filter_changed", { category: cat });
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Grouped, category-led view (default) */}
      {grouped &&
        groups.map((group, i) => (
          <section key={group.cat} className={i % 2 === 0 ? "bg-mist" : "bg-surface"}>
            <div className="mx-auto max-w-7xl px-5 py-12">
              <h2 className="text-2xl md:text-3xl">{ASSET_CATEGORIES[group.cat].label}</h2>
              <p className="mt-2 max-w-2xl text-muted">{ASSET_CATEGORIES[group.cat].blurb}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onClickCapture={onCardClick}>
                {group.assets.map((a) => <AssetCard key={a.slug} a={a} />)}
              </div>
            </div>
          </section>
        ))}

      {/* Flat, filtered/searched view */}
      {!grouped && (
        <section className="bg-mist">
          <div className="mx-auto max-w-7xl px-5 py-12">
            <p className="text-sm text-body">
              {resultCount} {resultCount === 1 ? "resource" : "resources"}
              {searching ? <> for &ldquo;{query.trim()}&rdquo;</> : <> · {ASSET_CATEGORIES[active as AssetCategory].label}</>}
            </p>
            {resultCount === 0 ? (
              <div className="mt-6 rounded-xl border border-line bg-white p-6 shadow-soft">
                <p className="font-display text-lg font-bold text-ink">No resources match &ldquo;{query.trim()}&rdquo; yet.</p>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-body">
                  Try a broader term, or browse the{" "}
                  <Link href="/tools/" className="font-semibold text-emerald-deep underline">free tools</Link> and{" "}
                  <Link href="/guides/" className="font-semibold text-emerald-deep underline">guides</Link>. Missing a resource you need?{" "}
                  <a href="mailto:hello@ramsdocs.co.uk" className="font-semibold text-emerald-deep underline">Tell us</a> — most-requested gets built first.
                </p>
              </div>
            ) : (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onClickCapture={onCardClick}>
                {filtered.map((a) => <AssetCard key={a.slug} a={a} />)}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function AssetCard({ a }: { a: PracticalAsset }) {
  return (
    <Link href={`/assets/${a.slug}/`} className="card lift shadow-soft p-6">
      <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{ASSET_CATEGORIES[a.category].label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-ink">{a.title}</div>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted">{a.description}</p>
      <div className="mt-4 text-sm font-semibold text-emerald-deep">View &amp; download &rarr;</div>
    </Link>
  );
}

function Pill({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
        on ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-line-2 hover:text-ink"
      }`}
    >
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}
